import { 
  EmailData, 
  SaveOptions, 
  SaveEmailRequest,
  SaveEmailResponse,
  ListEmailsResponse,
  SaveOptionsResponse,
  EmailPreviewResponse 
} from '../types/email.types';

export class EmailSaveAPI {
  private baseUrl = process.env.REACT_APP_API_URL;
  
  constructor(private getAccessToken: () => Promise<string>) {}

  async getSaveOptions(): Promise<SaveOptionsResponse> {
    const response = await fetch(`${this.baseUrl}/api/emails/options`, {
      headers: {
        'Authorization': `Bearer ${await this.getAccessToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch save options');
    }

    return response.json();
  }

  async saveEmail(emailData: EmailData, saveOptions: SaveOptions): Promise<SaveEmailResponse> {
    const request: SaveEmailRequest = { emailData, saveOptions };
    
    const response = await fetch(`${this.baseUrl}/api/emails/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this.getAccessToken()}`
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to save email');
    }

    return response.json();
  }

  async listEmails(): Promise<ListEmailsResponse> {
    const response = await fetch(`${this.baseUrl}/api/emails/list`, {
      headers: {
        'Authorization': `Bearer ${await this.getAccessToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch email list');
    }

    return response.json();
  }

  async downloadEmail(fileId: string): Promise<void> {
    const token = await this.getAccessToken();
    const downloadUrl = `${this.baseUrl}/api/emails/download/${fileId}`;
    
    // Create a temporary link with auth header
    const response = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download email');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'email.eml';

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async previewEmail(fileId: string): Promise<EmailPreviewResponse> {
    const response = await fetch(`${this.baseUrl}/api/emails/preview/${fileId}`, {
      headers: {
        'Authorization': `Bearer ${await this.getAccessToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to preview email');
    }

    return response.json();
  }
}

// Hook for using the API in components
export function useEmailSaveAPI() {
  const getAccessToken = async () => {
    // This will be provided by the identity context
    const token = await (window as any).getAccessToken?.();
    if (!token) {
      throw new Error('No access token available');
    }
    return token;
  };
  
  return new EmailSaveAPI(getAccessToken);
}
